import { IS_MOBILE } from '@constants/global.js';
import UIIcon from '@icons/UIIcon.js';
import MainDrawer from '@layouts/Drawer.js';
import ProjectsList from '@pages/Projects/index.js';
import { route } from '@router/index.js';
import DrawerState from '@states/DrawerState.js';
import OrganizationState from '@states/Organization.js';
import UserState from '@states/User.js';
import { UIButton, UIDiv } from '@ui';
import { logout } from '@utils/logoutHelper.js';
import { getSharedProjectId } from '@utils/projectUtils.js';
import OrganisationSwitcher from './OrganisationSwitcher.js';

const extraClass =
  'p-4 md:p-2 rounded hover:bg-gray-300 transition-colors flex justify-center';

const navLinksFirst = [
  {
    href: '/spatial-vision',
    icon: 'scan-eye',
    label: 'VLM',
    component: null,
    requiresUser: true,
  },
  {
    href: '/projects',
    icon: 'projects',
    label: 'Projects',
    component: ProjectsList,
  },
];

export default class MobileNavbar extends UIDiv {
  constructor() {
    super();
    this.setId('mobile-navbar');
    this.setClass(
      'flex justify-evenly items-center p-1 border-t border-gray-200 z-50 bg-white',
    );
    this.addClass('navbar');
    this.activeLink = null;
    this.user = UserState.user;
    this.componentCache = {};
    this.orgSwitcherMobile = null; // Store reference to org switcher

    this.render();

    UserState.subscribe(user => {
      this.user = user;
      this.render();
    }).bind(this);
    OrganizationState.subscribe(orgs => {
      this.orgs = orgs;
      // Update the org switcher with new organizations
      if (this.orgSwitcherMobile) {
        this.orgSwitcherMobile.setOrganizations(orgs);
      }
      this.addMobileSettings();
    }).bind(this);
  }

  render() {
    // Clear existing content
    this.clear();
    // Retained VLM and Projects navigation
    this.addNavItems(this, navLinksFirst);

    // Add org switcher in the middle
    this.orgSwitcherMobile = new OrganisationSwitcher({
      orgs: this.orgs,
      placement: 'top',
    });
    this.orgSwitcherMobile.setClass(
      'mobile-org-switcher relative size-14 flex justify-center items-center',
    );
    if (this.user) this.add(this.orgSwitcherMobile);

    this.addMobileSettings();
  }

  addNavItems(container, navLinks) {
    navLinks.forEach(link => {
      const button = this.createNavButton(
        link.href,
        link.icon,
        link.component,
        link.requiresUser,
      );
      if (!button) return; // Skip if button is not created (e.g., requiresUser is false)
      button.setAttribute('title', link.label);
      button.setAttribute('aria-label', link.label);
      container.add(button);
    });
  }

  createNavButton(href, icon, Component, requiresUser) {
    if (requiresUser && !this.user) return;

    // Special case: allow projects button for guest users if there's a shared project
    if (href === '/projects' && !this.user) {
      const sharedProjectId = getSharedProjectId();
      if (!sharedProjectId) return; // No shared project, hide projects button for guest users
    }

    const button = new UIButton('');
    button.setClass(extraClass);
    button.setAttribute('data-href', href);
    const isActive = href === this.getCurrentActivePage();
    if (isActive) {
      button.addClass('bg-gray-300');
    }
    const iconColor = 'none';
    const strokeColor = isActive ? 'var(--o-hive-primary-100)' : 'black';
    const iconImg = new UIIcon(icon, {
      color: iconColor,
      size: 22,
      strokeColor: strokeColor,
    });
    iconImg.setClass('w-8 h-8');
    button.add(iconImg);

    button.onClick(() => this.handleNavClick(button, href, Component));
    return button;
  }

  handleNavClick(button, href, Component) {
    if (this.activeLink) {
      this.activeLink.removeClass('bg-gray-300');
      const icon = this.activeLink.find('svg')[0];
      if (icon) {
        icon.dom.setAttribute('stroke', 'black');
      }
    }

    button.addClass('bg-gray-300');
    const icon = button.find('svg')[0];
    if (icon) {
      icon.dom.setAttribute('stroke', 'var(--o-hive-primary-100)');
    }
    this.activeLink = button;

    if (href === '/logout') {
      logout();
      return;
    }

    // Special handling for projects
    if (href === '/projects') {
      const sharedProjectId = getSharedProjectId();
      if (sharedProjectId && Component) {
        // For mobile with shared project, show the component directly
        if (!this.componentCache['projectslist']) {
          this.componentCache['projectslist'] = new Component();
        }

        const existingDrawer = DrawerState.getContent('projectslist');
        if (!existingDrawer) {
          MainDrawer.addContent(
            this.componentCache['projectslist'],
            'projectslist',
          );
        }

        MainDrawer.collapseSidebar('projectslist', true);
        return;
      }
    }

    // Keep the existing Projects drawer behavior.
    if (Component === ProjectsList) {
      // On desktop, components go to drawer
      // On mobile all is page
      if (!IS_MOBILE()) {
        const keyMap = {
          [ProjectsList.name.toLocaleLowerCase()]: 'projectslist',
        };
        const componentKey = keyMap?.[Component.name.toLocaleLowerCase()];

        if (!this.componentCache[componentKey]) {
          this.componentCache[componentKey] = new Component();
        }

        const existingDrawer = DrawerState.getContent(componentKey);
        if (!existingDrawer) {
          MainDrawer.addContent(
            this.componentCache[componentKey],
            componentKey,
          );
        }

        MainDrawer.collapseSidebar(componentKey, true);
      } else {
        route(href);
      }
    } else {
      route(href);
    }
  }

  addMobileSettings() {
    const existingContainer = this.find('.mobile-settings-container')[0];
    if (existingContainer) existingContainer.remove();
    if (!this.user) return;

    const mobileContainer = new UIDiv();
    mobileContainer.setClass('mobile-settings-container');
    const button = this.createNavButton('/logout', 'logout', null, true);
    button.setAttribute('title', 'Logout');
    button.setAttribute('aria-label', 'Logout');
    mobileContainer.add(button);
    this.add(mobileContainer);
  }

  getCurrentActivePage() {
    return window.location.pathname;
  }
}
